import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import Common from "../../based/Common";
import { IMAGEABLE_TYPE, NOTIFY } from "../../based/Constants";
import { ConfirmDialog } from "../../based/Dialog";
import CommonModal from "../../based/Modal";
import { Notify } from "../../based/Notify";
import Request from "../../based/Request";
import Textbox from "../../based/inputs/Textbox";
import useFormValidate from "../../hooks/useFormValidate";
import EmbroideryServices from "../../based/services/EmbroideryServices";

const DEFAULT_SIZES_MM = [50, 80, 120, 150, 200];

function _newSizeRow(sizeMm) {
    return { id: 0, sizeLabel: sizeMm ? `${sizeMm}mm` : "", dstFileUrl: "", dstFileName: "", pngFileUrl: "", pngFileName: "" };
}

function _newEffect(name) {
    return { id: 0, name: name || "", ebmFileUrl: "", ebmFileName: "", sizes: DEFAULT_SIZES_MM.map((mm) => _newSizeRow(mm)) };
}

function _newPattern() {
    return { id: 0, name: "", imageUrl: "", tags: [], effects: [_newEffect("Cơ bản")] };
}

function PatternUploadModal(props) {
    const [pattern, setPattern] = useState(_newPattern());
    const [activeEffectIndex, setActiveEffectIndex] = useState(0);
    const [tagInput, setTagInput] = useState("");
    const [isBusy, setIsBusy] = useState(false);
    const { addRef, isValid } = useFormValidate();

    useEffect(() => {
        if (props.id > 0) _getData(props.id);
        else {
            setPattern(_newPattern());
            setActiveEffectIndex(0);
        }
    }, [props.id]);

    async function _getData(id) {
        setIsBusy(true);
        let [err, data] = await EmbroideryServices.GetPatternById(id);
        if (!err && data) {
            setPattern({
                id: data.id,
                name: data.name || "",
                imageUrl: data.imageUrl || "",
                tags: data.tags || [],
                effects: data.effects && data.effects.length > 0 ? data.effects.map((e) => ({ ...e, sizes: e.sizes || [] })) : [_newEffect("Cơ bản")],
            });
            setActiveEffectIndex(0);
        }
        setIsBusy(false);
    }

    function _handleAddTag() {
        let value = (tagInput || "").trim();
        if (!value) return;
        if (pattern.tags.some((t) => (t.tagName || "").toLowerCase() === value.toLowerCase())) {
            setTagInput("");
            return;
        }
        setPattern({ ...pattern, tags: [...pattern.tags, { id: 0, tagName: value }] });
        setTagInput("");
    }

    function _handleTagKeyDown(e) {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            _handleAddTag();
        }
    }

    function _handleRemoveTag(idx) {
        let tags = [...pattern.tags];
        tags.splice(idx, 1);
        setPattern({ ...pattern, tags });
    }

    function _handleAddEffect() {
        let effects = [...pattern.effects, _newEffect(`Hiệu ứng ${pattern.effects.length + 1}`)];
        setPattern({ ...pattern, effects });
        setActiveEffectIndex(effects.length - 1);
    }

    function _handleRemoveEffect(idx) {
        if (pattern.effects.length <= 1) {
            Notify(NOTIFY.WARNING, "Chú ý", "Họa tiết phải có ít nhất 1 hiệu ứng");
            return;
        }
        ConfirmDialog("Xóa hiệu ứng", "Xác nhận xóa hiệu ứng?", () => {
            return new Promise(async (resolve) => {
                let effects = [...pattern.effects];
                effects.splice(idx, 1);
                setPattern({ ...pattern, effects });
                setActiveEffectIndex(activeEffectIndex >= effects.length ? effects.length - 1 : activeEffectIndex > idx ? activeEffectIndex - 1 : 0);
                resolve({ title: "Thành công", msg: "Đã xóa hiệu ứng." });
            });
        });
    }

    function _handleChangeEffectName(idx, value) {
        let effects = [...pattern.effects];
        effects[idx] = { ...effects[idx], name: value };
        setPattern({ ...pattern, effects });
    }

    function _handleAddSizeRow(effectIdx) {
        let effects = [...pattern.effects];
        effects[effectIdx] = { ...effects[effectIdx], sizes: [...effects[effectIdx].sizes, _newSizeRow()] };
        setPattern({ ...pattern, effects });
    }

    function _handleRemoveSizeRow(effectIdx, sizeIdx) {
        let effects = [...pattern.effects];
        let sizes = [...effects[effectIdx].sizes];
        sizes.splice(sizeIdx, 1);
        effects[effectIdx] = { ...effects[effectIdx], sizes };
        setPattern({ ...pattern, effects });
    }

    function _handleChangeSizeLabel(effectIdx, sizeIdx, value) {
        let effects = [...pattern.effects];
        let sizes = [...effects[effectIdx].sizes];
        sizes[sizeIdx] = { ...sizes[sizeIdx], sizeLabel: value };
        effects[effectIdx] = { ...effects[effectIdx], sizes };
        setPattern({ ...pattern, effects });
    }

    async function _handleUploadEbm(effectIdx, e) {
        // Cache the input before awaits because React may clear the event target after async work.
        const input = e.target;
        let file = input.files[0];
        if (!file) return;
        setIsBusy(true);
        let res = await Request.UploadDocument(file);
        setIsBusy(false);
        if (res && res.data) {
            setPattern((prev) => {
                let effects = [...prev.effects];
                effects[effectIdx] = { ...effects[effectIdx], ebmFileUrl: res.data.url, ebmFileName: file.name };
                return { ...prev, effects };
            });
        } else {
            Notify(NOTIFY.ERROR, NOTIFY.ERROR, "Upload file .EBM thất bại");
        }
        input.value = "";
    }

    async function _handleUploadDst(effectIdx, sizeIdx, e) {
        const input = e.target;
        let file = input.files[0];
        if (!file) return;
        setIsBusy(true);
        let res = await Request.UploadDocument(file);
        setIsBusy(false);
        if (res && res.data) {
            setPattern((prev) => {
                let effects = [...prev.effects];
                let sizes = [...effects[effectIdx].sizes];
                sizes[sizeIdx] = { ...sizes[sizeIdx], dstFileUrl: res.data.url, dstFileName: file.name };
                effects[effectIdx] = { ...effects[effectIdx], sizes };
                return { ...prev, effects };
            });
        } else {
            Notify(NOTIFY.ERROR, NOTIFY.ERROR, "Upload file .DST thất bại");
        }
        input.value = "";
    }

    async function _handleUploadPng(effectIdx, sizeIdx, e) {
        const input = e.target;
        let file = input.files[0];
        if (!file) return;
        setIsBusy(true);
        let res = await Request.UploadImage(file, IMAGEABLE_TYPE.StockPhoto);
        setIsBusy(false);
        if (res && res.data && res.data.imageUrl) {
            setPattern((prev) => {
                let effects = [...prev.effects];
                let sizes = [...effects[effectIdx].sizes];
                sizes[sizeIdx] = { ...sizes[sizeIdx], pngFileUrl: res.data.imageUrl, pngFileName: file.name };
                effects[effectIdx] = { ...effects[effectIdx], sizes };
                return { ...prev, effects };
            });
        } else {
            Notify(NOTIFY.ERROR, NOTIFY.ERROR, "Upload file .PNG thất bại");
        }
        input.value = "";
    }

    async function _handleSave() {
        if (!isValid()) return;
        for (let i = 0; i < pattern.effects.length; i++) {
            let effect = pattern.effects[i];
            if (!effect.name || !effect.name.trim()) {
                setActiveEffectIndex(i);
                Notify(NOTIFY.WARNING, "Chú ý", "Vui lòng nhập tên cho tất cả hiệu ứng");
                return;
            }
            if (!effect.ebmFileUrl) {
                setActiveEffectIndex(i);
                Notify(NOTIFY.WARNING, "Chú ý", `Vui lòng upload file Wilcom EBM cho hiệu ứng "${effect.name}"`);
                return;
            }
        }

        setIsBusy(true);
        let payload = {
            id: pattern.id,
            name: pattern.name,
            tags: pattern.tags,
            effects: pattern.effects.map((e) => ({
                id: e.id,
                name: e.name,
                ebmFileUrl: e.ebmFileUrl,
                ebmFileName: e.ebmFileName,
                sizes: (e.sizes || [])
                    .filter((s) => s.sizeLabel && s.sizeLabel.trim())
                    .map((s) => ({
                        id: s.id,
                        sizeLabel: s.sizeLabel,
                        dstFileUrl: s.dstFileUrl,
                        dstFileName: s.dstFileName,
                        pngFileUrl: s.pngFileUrl,
                        pngFileName: s.pngFileName,
                    })),
            })),
        };
        let [err, data] = await EmbroideryServices.CreateUpdatePattern(payload);
        setIsBusy(false);
        if (!err && data) {
            Notify(NOTIFY.SUCCESS, NOTIFY.SUCCESS, "Lưu thành công");
            props.saveSucceed();
        } else {
            Notify(NOTIFY.ERROR, NOTIFY.ERROR, "Lưu thất bại");
        }
    }

    let activeEffect = pattern.effects[activeEffectIndex];

    return (
        <CommonModal show={props.isShowModal} id={Common.generateGuid()} size="lg" isBusy={isBusy} busyMsg="Đang xử lý..." title={props.id > 0 ? "Sửa họa tiết thêu" : "Thêm họa tiết thêu"} onSave={_handleSave} onClose={() => props.onClose()}>
            <div className="form-group">
                <label htmlFor="name" className="required">
                    Tên họa tiết thêu
                </label>
                <Textbox id="name" name="name" required ref={addRef} className="form-control" onChanged={(name, value) => setPattern({ ...pattern, name: value })} value={pattern.name} />
            </div>
            <div className="form-group">
                <label htmlFor="tag">Tag</label>
                <div className="d-flex flex-wrap align-items-center border rounded p-2">
                    {pattern.tags.map((tag, idx) => (
                        <span className="badge badge-secondary m-1 p-2" key={idx}>
                            {tag.tagName} <i className="fa fa-times ml-1 cursor-pointer" onClick={() => _handleRemoveTag(idx)}></i>
                        </span>
                    ))}
                    <input
                        id="tag"
                        type="text"
                        className="border-0 flex-grow-1"
                        style={{ minWidth: 120, outline: "none" }}
                        placeholder="Thêm tag"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={_handleTagKeyDown}
                        onBlur={_handleAddTag}
                    />
                </div>
            </div>

            <label className="font-weight-bold">File theo hiệu ứng và kích thước</label>
            <ul className="nav nav-tabs tabs-bordered">
                {pattern.effects.map((effect, idx) => (
                    <li key={idx} className="nav-item">
                        <a className={activeEffectIndex == idx ? "nav-link active" : "nav-link btn-light"} onClick={() => setActiveEffectIndex(idx)}>
                            <span>{effect.name || `Hiệu ứng ${idx + 1}`}</span>
                            {pattern.effects.length > 1 && (
                                <i
                                    className="fa fa-times ml-2 cursor-pointer"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        _handleRemoveEffect(idx);
                                    }}
                                ></i>
                            )}
                        </a>
                    </li>
                ))}
                <li className="nav-item">
                    <a className="nav-link btn-light text-custom font-weight-bold" onClick={_handleAddEffect}>
                        Thêm +
                    </a>
                </li>
            </ul>

            {activeEffect && (
                <div className="p-10">
                    <div className="row">
                        <div className="col-6 form-group">
                            <label className="required">File Wilcom EBM</label>
                            <div className="d-flex align-items-center">
                                <input type="file" id={`ebm-file-${activeEffectIndex}`} className="d-none" onChange={(e) => _handleUploadEbm(activeEffectIndex, e)} />
                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => document.getElementById(`ebm-file-${activeEffectIndex}`).click()}>
                                    Choose file
                                </button>
                                <span className="ml-2">{activeEffect.ebmFileName || "Chưa upload"}</span>
                            </div>
                        </div>
                        <div className="col-6 form-group">
                            <label className="required">Tên hiệu ứng</label>
                            <input type="text" className="form-control" value={activeEffect.name} onChange={(e) => _handleChangeEffectName(activeEffectIndex, e.target.value)} />
                        </div>
                    </div>

                    <table className="table table-bordered">
                        <thead>
                            <tr className="text-center">
                                <th style={{ width: 140 }}>Kích thước</th>
                                <th>File .DST</th>
                                <th>
                                    File .PNG
                                    <div className="text-muted font-weight-normal">(Hiển thị tên web)</div>
                                </th>
                                <th style={{ width: 70 }}>Preview</th>
                                <th style={{ width: 90 }}>Hành động</th>
                            </tr>
                        </thead>
                        <tbody>
                            {activeEffect.sizes.map((row, sizeIdx) => (
                                <tr key={sizeIdx}>
                                    <td>
                                        <input type="text" className="form-control text-center" placeholder="Nhập kích thước" value={row.sizeLabel} onChange={(e) => _handleChangeSizeLabel(activeEffectIndex, sizeIdx, e.target.value)} />
                                    </td>
                                    <td className="text-center">
                                        <input type="file" id={`dst-file-${activeEffectIndex}-${sizeIdx}`} className="d-none" onChange={(e) => _handleUploadDst(activeEffectIndex, sizeIdx, e)} />
                                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => document.getElementById(`dst-file-${activeEffectIndex}-${sizeIdx}`).click()}>
                                            Choose file
                                        </button>
                                        <div className={row.dstFileName ? "" : "text-muted"}>{row.dstFileName || "Chưa upload"}</div>
                                    </td>
                                    <td className="text-center">
                                        <input type="file" id={`png-file-${activeEffectIndex}-${sizeIdx}`} className="d-none" accept="image/png" onChange={(e) => _handleUploadPng(activeEffectIndex, sizeIdx, e)} />
                                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => document.getElementById(`png-file-${activeEffectIndex}-${sizeIdx}`).click()}>
                                            Choose file
                                        </button>
                                        <div className={row.pngFileName ? "" : "text-muted"}>{row.pngFileName || "Chưa upload"}</div>
                                    </td>
                                    <td className="text-center">{row.pngFileUrl ? <img src={row.pngFileUrl} alt="" style={{ width: 50, height: 50, objectFit: "contain" }} /> : "-"}</td>
                                    <td className="text-center">
                                        <button type="button" className="btn btn-danger btn-sm" onClick={() => _handleRemoveSizeRow(activeEffectIndex, sizeIdx)}>
                                            Xóa
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="add-item" style={{ color: "#64C5B1", fontSize: 16, fontWeight: 600, cursor: "pointer" }} onClick={() => _handleAddSizeRow(activeEffectIndex)}>
                        Thêm kích thước +
                    </div>
                </div>
            )}
        </CommonModal>
    );
}

PatternUploadModal.propTypes = {
    id: PropTypes.number,
    isShowModal: PropTypes.bool,
    onClose: PropTypes.func,
    saveSucceed: PropTypes.func,
};

export default PatternUploadModal;
